'use strict';

// external imports

// shared imports
import LocalizableError from '~/shared/src/base/LocalizableError';
import metaModel from "~/shared/src/utilities/metaModel";
import {__} from '~/shared/src/utilities/messageCatalog';

// backend imports


// type definitions
export type StatusAttributes = {
    id? : number,
    name? : string,
    description? : string,
    color? : string,
    uuid? : string,
    modifierUserId? : string,
    modificationTime? : NDate,
    validFromTime? : NDate,
    validToTime? : NDate
};

export type StatusAttributesCollection = Array<StatusAttributes>;

export type StatusAttributesRequired = keyof Pick<StatusAttributes, 'id' | 'name'>;

export type StatusAttributesSettable = Pick<StatusAttributes, 'name' | 'description' | 'color'>;

/**
 * The Status class.
 */
class Status {

    /**
     * The unique Id counter for Statuss
     */
    static uniqueIdCount = -1;


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ID of the Status
     */
    readonly id : number;

    /**
     * The Name of the Status
     */
    protected _name : string = '';
    get name() {
        return this._name;
    }

    set name( value : string ) {
        this.validateName( value );

        this.handleModified( 'name', value );

        this._name = value;
    }

    /**
     * The description of the Status
     */
    protected _description : string = '';
    get description() {
        return this._description;
    }

    set description( value : string ) {
        this.handleModified( 'description', value );

        this._description = value;
    }

    /**
     * The color to display the Status or Items of the Status with
     */
    protected _color : string = '';
    get color() {
        return this._color;
    }

    set color( value : string ) {
        this.handleModified( 'color', value );

        this._color = value;
    }

    /**
     * The unique spoken id of the Status
     */
    readonly uuid : string = '';

    /**
     * The UserID of the last modifier of the Status
     */
    readonly modifierUserId : string = '';

    /**
     * The last modification date of the Status
     */
    readonly modificationTime : NDate = null;

    /**
     * The valid from date of a History-Status
     */
    readonly validFromTime : NDate = null;

    /**
     * The valid to date of a History-Status
     */
    readonly validToTime : NDate = null;


    /*** ALIASES ***********************************************************************************************************************/

    get label() : string {
        let msgId = `YN.base.status.${this._name}`;

        if ( this._name === 'deleted' ) {
            msgId = 'YN.base.status.deleted';
        }

        let retVal = __( msgId );

        if ( retVal === '' || retVal === msgId ) {
            retVal = this._name;
        }

        return retVal;
    }


    /*************************************************************************************************************************************/

    /**
     * The database schema this Item belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * Map containing the modified attributes with their initial values
     */
    protected _attributesModified : Map<keyof StatusAttributesSettable, AttributeValue>;
    get attributesModified() {
        return this._attributesModified;
    }

    /**
     * The constructor.
     *
     * @param attributes                An object containing attribute-value pairs for this
     * @param schema                    The database schema the Item belongs to
     */
    constructor( attributes : StatusAttributes, schema : Schema = global.schema ) {

        if ( attributes.id && ( attributes.id === 0 || attributes.id < -1 ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attributes.validation.id.invalid' );
        }

        this._schema = schema;

        if ( attributes.id ) {
            this.id = attributes.id;
        }
        else {
            this.id = Status.uniqueIdCount--;
        }

        if ( attributes.name ) {
            this._name = attributes.name;
        }

        if ( attributes.description ) {
            this._description = attributes.description;
        }

        if ( attributes.color ) {
            this._color = attributes.color;
        }

        if ( attributes.uuid ) {
            this.uuid = attributes.uuid;
        }

        if ( attributes.modifierUserId ) {
            this.modifierUserId = attributes.modifierUserId;
        }
        if ( attributes.modificationTime ) {
            this.modificationTime = attributes.modificationTime;
        }

        if ( attributes.validFromTime ) {
            this.validFromTime = attributes.validFromTime;
        }
        if ( attributes.validToTime ) {
            this.validToTime = attributes.validToTime;
        }

        this._attributesModified = new Map();
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param includeAttributes   The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : StatusAttributes {
        // required attributes
        const retVal : StatusAttributes = {
            id: this.id
        };

        if ( includeAttributes === 'all' || this._attributesModified.has( 'name' ) ) {
            retVal.name = this._name;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'description' ) ) {
            retVal.description = this._description;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'color' ) ) {
            retVal.color = this._color;
        }

        // not settable attributes
        if ( includeAttributes === 'all' ) {
            retVal.uuid = this.uuid;
            retVal.modifierUserId = this.modifierUserId;

            // only active attributes
            if ( !this.isHistorized() ) {
                retVal.modificationTime = this.modificationTime;
            }

            // only history attributes
            else {
                retVal.validFromTime = this.validFromTime;
                retVal.validToTime = this.validToTime;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        return `Status '${this.name}' (ID: ${this.id})`;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() {
        if ( this._attributesModified.has( 'name' ) ) {
            this._name = this._attributesModified.get( 'name' ) as string;
        }

        if ( this._attributesModified.has( 'description' ) ) {
            this._description = this._attributesModified.get( 'description' ) as string;
        }

        if ( this._attributesModified.has( 'color' ) ) {
            this._color = this._attributesModified.get( 'color' ) as string;
        }

        this._attributesModified = new Map();
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     */
    clone( attributes : StatusAttributesSettable = {} ) : Status {
        const clone : Status = Object.assign( {}, this );

        // apply data
        if ( attributes.name ) {
            clone.name = attributes.name;
        }

        if ( attributes.description ) {
            clone.description = attributes.description;
        }

        if ( attributes.color ) {
            clone.color = attributes.color;
        }

        return clone;
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        return this.id < 0;
    }

    /**
     * Checks if this is historized.
     */
    isHistorized() : boolean {
        return this.validFromTime !== null;
    }

    /**
     * Checks if this or an EnumProperty or a Property of this is modified.
     */
    isModified() : boolean {
        return this._attributesModified.size > 0;
    }

    /**
     * Checks if the given attribute is modified or not.
     *
     * @param attribute        The attribute to check if it is modified
     */
    isAttributeModified( attribute : keyof StatusAttributesSettable ) : boolean {
        return this._attributesModified.has( attribute );
    }

    /**
     * Handles the modified attributes list.
     *
     * @param attribute         The attribute
     * @param value             The value for the attribute
     */
    protected handleModified( attribute : keyof StatusAttributesSettable, value : AttributeValue ) {
        // set as modified
        if ( !this._attributesModified.has( attribute ) && value !== this[ attribute ] ) {
            this._attributesModified.set( attribute, this[ attribute ] );
        }

        // unset as modified
        else if ( this._attributesModified.has( attribute ) && value === this._attributesModified.get( attribute ) ) {
            this._attributesModified.delete( attribute );
        }
    }

    /**
     * Validates the name for this
     */
    protected validateName( name : string ) {
        const status = metaModel[ this._schema ].getStatus( name );

        if ( status && status.id !== this.id ) {
            throw new LocalizableError( 'YN.base.dataObject.status.attribute.validation.name.isNotUnique' );
        }
    }

}

export default Status;
